<template>
  <div class="app-list feature-flags__wrp">
    <div class="pull-right actions-wrap">
      <permission-wrapper
        skip-alert
        page-name="feature-flags"
        resource-name="feature_access"
        action-name="create">
        <feature-flags-editor
          :details="defaultDetails"
          :loading="updating"
          :title="'feature-flags.lbl-add' | globalize"
          @add="(payload) => onAction(ACTION_TYPES.create, payload)"
        />
      </permission-wrapper>
    </div>

    <a-table
      class="ant-table"
      size="small"
      :data-source="list"
      :columns="columns"
      :row-key="(record) => record.flag"
      :pagination="false"
      :loading="isPending"
      :row-class-name="(_record, index) => (index % 2 === 1 ? 'ant-table-striped' : null)"
    >
      <template
        slot="flag-column"
        slot-scope="flag">
        <copyable :copy-text="flag">
          {{ flag }}
        </copyable>
      </template>

      <template
        slot="description-column"
        slot-scope="description">
        <copyable :copy-text="description">
          {{ description }}
        </copyable>
      </template>

      <template
        slot="status-column"
        slot-scope="disabled,item">
        <a-switch
          :checked="!item.disabled"
          @change="(value) => onAction(ACTION_TYPES.update, { item, disabled: value })" />
        {{
          (!item.disabled ? 'feature-flags.status-enabled' :
            'feature-flags.status-disabled') | globalize
        }}
      </template>

      <template
        slot="actions-column"
        slot-scope="domain,item"
      >
        <permission-wrapper
          skip-alert
          page-name="feature-flags"
          resource-name="feature_access"
          action-name="delete">
          <feature-flags-delete :on-click="() => onAction(ACTION_TYPES.delete, item)" />
        </permission-wrapper>
      </template>
    </a-table>
  </div>
</template>

<script>
import { ErrorHandler } from '@/js/helpers/error-handler'
import { api } from '@/api'
import Copyable from '@/vue/common/copyable/Copyable'
import FeatureFlagsEditor from '@/vue/pages/feature-flags/components/editor'
import ASwitch from 'ant-design-vue/lib/switch'
import ATable from 'ant-design-vue/lib/table'
import { globalize } from '@/vue/filters/globalize'
import { Bus } from '@/js/helpers/event-bus'
import FeatureFlagsDelete from './components/delete'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import { ACTION_TYPES } from '@/js/const/action-types'
import _ from 'lodash'

const defaultDetails = {
  domain: '',
  key: '',
  clientId: '',
}

const endpoint = '/featureflags'
const type = 'feature_flag'

export default {
  name: 'feature-flags',
  components: {
    Copyable,
    FeatureFlagsDelete,
    FeatureFlagsEditor,
    PermissionWrapper,
    ATable,
    ASwitch,
  },
  data () {
    return {
      isPending: false,
      isLoaded: false,
      updating: false,
      disabledItem: false,
      ACTION_TYPES,
      list: [],
      defaultDetails,
    }
  },
  computed: {
    columns () {
      return [
        {
          width: 200,
          title: globalize('feature-flags.lbl-flag'),
          dataIndex: 'flag',
          key: 'flag',
          scopedSlots: { customRender: 'flag-column' },
          sorter: (a, b) => a.flag.length - b.flag.length,
        },
        {
          title: globalize('feature-flags.lbl-description'),
          align: 'center',
          dataIndex: 'description',
          key: 'description',
          scopedSlots: { customRender: 'description-column' },
        },
        {
          title: globalize('feature-flags.lbl-status'),
          dataIndex: 'status',
          key: 'status',
          scopedSlots: { customRender: 'status-column' },
          sorter: (a, b) => a.disabled - b.disabled,
        },
        {
          align: 'right',
          title: globalize('feature-flags.lbl-actions'),
          dataIndex: 'actions',
          key: 'actions',
          scopedSlots: { customRender: 'actions-column' },
        },
      ]
    },
  },
  watch: {
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
  },
  async created () {
    await this.getList()
  },
  methods: {
    toggleDisable (item) {
      item.disabled = !item.disabled
      return !item.disabled
    },
    async getList () {
      this.isLoaded = false
      this.isPending = true
      let response = {}
      try {
        response = await api.getWithSignature(endpoint)
        this.list = response.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
        this.isPending = false
      }

      return response
    },
    async onAction (actionType, payload) {
      this.isPending = true

      try {
        switch (actionType) {
          case ACTION_TYPES.create: {
            await this.onCreate(payload)
            Bus.success('feature-flags.add-success-message')
            _.delay(this.getList, 500)
            break
          }
          case ACTION_TYPES.update: {
            await this.onUpdate(payload)
            Bus.success('feature-flags.update-success-message')
            break
          }
          case ACTION_TYPES.delete: {
            await this.onDelete(payload)
            Bus.success('feature-flags.deleted-success-message')
            _.delay(this.getList, 500)
            break
          }
        }
      } catch (e) {
        switch (actionType) {
          case ACTION_TYPES.update: {
            this.toggleDisable(payload)
            break
          }
          default: {
            let errorMsg = e.detail[0].toUpperCase() + e.detail.slice(1, e.detail.length)
            ErrorHandler.processWithoutFeedback(e)
            Bus.error(errorMsg)
            break
          }
        }
      } finally {
        this.isPending = false
      }
    },
    async onCreate (item) {
      const { flag, description, status } = item
      item.disabled = status

      await api.postWithSignature(endpoint, JSON.stringify({
        data: {
          type,
          attributes: {
            flag,
            description,
            disabled: !status,
          },
        },
      }))
    },
    async onUpdate ({ item, disabled }) {
      const { type, flag, description } = item
      item.disabled = !disabled

      await api.putWithSignature(`${endpoint}/${flag}`, JSON.stringify({
        data: {
          type,
          attributes: {
            flag,
            description,
            disabled: !disabled,
          },
        },
      }))
    },
    async onDelete (item) {
      await api.deleteWithSignature(`${endpoint}/${item.flag}`)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~ant-design-vue/lib/switch/style/index.css';
@import '~@scss/variables';

.actions-wrap {
  margin-bottom: 1.5rem;
}

.ant-table {
  background-color: $col-ant-table-bg;
}
</style>
